import type { FormDialogRef } from '@/components'
import {
  Button,
  Checkbox,
  DatePicker,
  FAIcon,
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FormDialog,
} from '@/components'
import { cn } from '@/lib/utils'
import { dateFormat } from '@/utils/graphql'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { LOCK_ABSENCES_MUTATION } from '../mutations'
import {
  ABSENCE_LOCK_CONFIGS_QUERY,
  type ACTIVE_ABSENCE_LOCK_QUERY,
} from '../queries'
import { AbsenceLockConfigsList } from './absence-lock-configs-list'
import { LockActionsToggle } from './lock-actions-toggle'

const AbsenceLockDialogSchema = z.object({
  lockActions: z.number().min(0).max(7),
  adminOverride: z.boolean(),
  permanent: z.boolean(),
  manualUntil: z.date().nullable(),
})

export type AbsenceLockDialogForm = z.infer<typeof AbsenceLockDialogSchema>

export type ActiveAbsenceLock = ResultOf<
  typeof ACTIVE_ABSENCE_LOCK_QUERY
>['activeAbsenceLock']

interface Props {
  activeLock: ActiveAbsenceLock
  onCreateConfig(id?: string): void
}

export function AbsenceLockDialog({ activeLock, onCreateConfig }: Props) {
  const translations = useTranslate({
    header: 'absences.absence-lock-dialog.header',

    manualTitle: 'absences.absence-lock-dialog.manual.title',
    manualDescription: 'absences.absence-lock-dialog.manual.description',
    manualSubmitLock: 'absences.absence-lock-dialog.manual.submit-lock',
    manualSubmitUpdate: 'absences.absence-lock-dialog.manual.submit-update',

    configureTitle: 'absences.absence-lock-dialog.configure.title',
    configureDescription: 'absences.absence-lock-dialog.configure.description',

    createConfig: 'absences.absence-lock-dialog.lock-configs.create-new',

    lockActions: {
      label: 'absences.absence-lock-dialog.lock-actions.label',
      description: 'absences.absence-lock-dialog.lock-actions.description',
    },
    adminOverride: {
      label: 'absences.absence-lock-dialog.admin-override.label',
      description: 'absences.absence-lock-dialog.admin-override.description',
    },
    permanent: {
      label: 'absences.absence-lock-dialog.permanent.label',
      description: 'absences.absence-lock-dialog.permanent.description',
    },
    lockUntil: {
      label: 'absences.absence-lock-dialog.lock-until.label',
      description: 'absences.absence-lock-dialog.lock-until.description',
    },

    close: 'common.close',
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const form = useForm<AbsenceLockDialogForm>({
    resolver: zodResolver(AbsenceLockDialogSchema),
    defaultValues: {
      lockActions: activeLock?.lockActions ?? 6,
      adminOverride: activeLock?.adminOverride ?? false,
      permanent: activeLock?.permanent ?? false,
      manualUntil: null,
    },
  })

  const { data: configsData, loading: configsLoading } = useQuery(
    ABSENCE_LOCK_CONFIGS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    }
  )

  const configs = configsData?.absenceLockConfigs ?? []

  const [lock, { loading: lockLoading }] = useMutation(LOCK_ABSENCES_MUTATION, {
    refetchQueries: ['ActiveAbsenceLock'],
    awaitRefetchQueries: true,
    onCompleted(data) {
      if (!data.lockAbsences.ok) {
        window.alert('Failed to lock/unlock absences. Please try again.')
        return
      }

      dialogRef.current?.close()
    },
    onError(err) {
      // TODO proper error
      console.error('Error locking/unlocking absences:', err)
      window.alert('Failed to lock/unlock absences. Please try again.')
    },
  })

  function resetForm() {
    setTimeout(() => {
      form.reset()
    }, 300)
  }

  function handleBeginCreate() {
    dialogRef.current?.close()
    onCreateConfig()
  }

  async function handleSubmit(values: AbsenceLockDialogForm) {
    await lock({
      variables: {
        lockActions: values.lockActions,
        adminOverride: values.adminOverride,
        permanent: values.permanent,
        until: dateFormat(values.manualUntil),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    })
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      header={translations.header}
      trigger={
        <Button
          className={cn(!!activeLock && 'rounded-l-none border-l-[0.5px]')}
          variant="outline"
          size="icon"
        >
          <FAIcon className="text-xl" icon="lock-alt" />
        </Button>
      }
      cancelText={translations.close}
      noConfirmButton
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <h3 className="text-lg font-semibold ml-1">
            {translations.manualTitle}
          </h3>

          <p className="text-sm text-muted-foreground mt-1 ml-1">
            {translations.manualDescription}
          </p>

          <FieldGroup className="mt-4">
            <Controller
              name="lockActions"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <Field>
                  <FieldLabel>{translations.lockActions.label}</FieldLabel>

                  <FieldDescription>
                    {translations.lockActions.description}
                  </FieldDescription>

                  <FieldContent>
                    <LockActionsToggle value={value} onChange={onChange} />
                  </FieldContent>
                </Field>
              )}
            />

            <Controller
              name="adminOverride"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <Field>
                  <div className="flex items-center justify-start gap-2">
                    <FieldContent className="flex-0">
                      <Checkbox
                        id="admin-override"
                        value={value}
                        size="md"
                        onChange={onChange}
                      />
                    </FieldContent>

                    <FieldLabel htmlFor="admin-override">
                      {translations.adminOverride.label}
                    </FieldLabel>
                  </div>

                  <FieldDescription>
                    {translations.adminOverride.description}
                  </FieldDescription>
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="manualUntil"
              render={({ field: { value, onChange } }) => (
                <Field>
                  <FieldLabel optional>
                    {translations.lockUntil.label}
                  </FieldLabel>

                  <FieldDescription>
                    {translations.lockUntil.description}
                  </FieldDescription>

                  <DatePicker
                    value={value}
                    mode="single"
                    clearable
                    disabledDates={{
                      before: new Date(),
                    }}
                    onChange={onChange}
                  />
                </Field>
              )}
            />

            <Controller
              name="permanent"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <Field>
                  <div className="flex items-center justify-start gap-2">
                    <FieldContent className="flex-0">
                      <Checkbox
                        id="permanent"
                        value={value}
                        size="md"
                        onChange={onChange}
                      />
                    </FieldContent>

                    <FieldLabel htmlFor="permanent">
                      {translations.permanent.label}
                    </FieldLabel>
                  </div>

                  <FieldDescription>
                    {translations.permanent.description}
                  </FieldDescription>
                </Field>
              )}
            />
          </FieldGroup>

          <Button
            className="w-full mt-4"
            type="submit"
            size="lg"
            disabled={lockLoading}
          >
            {!!activeLock
              ? translations.manualSubmitUpdate
              : translations.manualSubmitLock}
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <h3 className="text-lg font-semibold ml-1">
            {translations.configureTitle}
          </h3>

          <p className="text-sm text-muted-foreground mt-1 ml-1">
            {translations.configureDescription}
          </p>

          <div className={cn(!!configs.length && 'mt-4')}>
            {configsLoading ? (
              <div className="flex justify-center">
                <Loader.Dots />
              </div>
            ) : (
              <AbsenceLockConfigsList configs={configs} />
            )}
          </div>

          <Button
            className="w-full mt-4"
            size="lg"
            type="button"
            onClick={handleBeginCreate}
          >
            {translations.createConfig}
          </Button>
        </div>
      </div>
    </FormDialog>
  )
}
